import React, { useEffect, useRef, useState } from "react"
import { Col, Row, Container, Button } from "react-bootstrap";
import { BsPencil, BsTrash3, BsImage } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Cookies from "js-cookie";
import { fetchDepartments, fetchEmails, fetchUser } from "../../redux/actions";
import { baseURL } from "../../helpers/baseURL";
import MainContainer from "../MainContainer";
import plus from "../../../css/img/plus.svg"
import TableView from '../TableView';
import ItemModal from "../ItemModal";
import { showDeleteConfirm } from "../ItemDelete";
import IncomingEmailTicketRecipient from "../IncomingEmailTicketRecipient";

const Emails = () => {

	const dispatch = useDispatch()
	const [showItemModal, setShowItemModal] = useState(false)
	const [formData, setFormData] = useState({});
	const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
	const [logoSettingsLoaded, setLogoSettingsLoaded] = useState(false);
	const [logoUploading, setLogoUploading] = useState(false);
	const logoInputRef = useRef(null);

	const single = 'Email';
	const itemType = 'emails';
	const prefixAPI = 'emails';

	const defaultFormInputs = {
		id: null,
		name: '',
		email_address: '',
		department: {}
	}

	const columns = [
		{ key: 'name', label: 'Name' },
		{ key: 'email_address', label: 'Email Address' },
		{ key: 'received', label: 'Received Emails' },
		{ key: 'department', label: 'Department', mode: 'badge' },
		{ key: '__actions__', label: '' }
	];

	const processRows = (rows) => {
		if (rows === undefined || Object.keys(rows).length === 0) {
			return [];
		}
		return rows.map(row => ({
			...row,
			__actions__: () => <>
				<Button onClick={() => showFormModal('Edit', row)} variant='tertiary' className='small-icon-button me-2 normal'><BsPencil /></Button>
				<Button onClick={() => showDeleteConfirm(row, single, prefixAPI, reduxRefresh)} variant='tertiary' className='small-icon-button danger'><BsTrash3 /></Button>
			</>
		}));
	};

	const reduxRefresh = () => {
		dispatch(fetchDepartments());
		dispatch(fetchEmails());
		dispatch(fetchUser());
	};

	const loadIncomingEmailSettings = () => {
		axios.get(`${baseURL}emails/incoming-settings`, {
			headers: { Authorization: `Bearer ${Cookies.get('access_token')}` },
		}).then((res) => {
			setCompanyLogoUrl(res.data?.incoming_email_logo_url ?? null);
		}).catch(() => {
			setCompanyLogoUrl(null);
		}).finally(() => {
			setLogoSettingsLoaded(true);
		});
	};

	useEffect(() => {
		reduxRefresh();
		loadIncomingEmailSettings();
	}, []);

	const openLogoPicker = () => {
		if (!logoUploading) {
			logoInputRef.current?.click();
		}
	};

	const handleLogoSelect = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const form = new FormData();
		form.append('logo', file);
		setLogoUploading(true);

		axios.post(`${baseURL}emails/incoming-logo`, form, {
			headers: {
				Authorization: `Bearer ${Cookies.get('access_token')}`,
				'Content-Type': 'multipart/form-data',
			},
		}).then((res) => {
			setCompanyLogoUrl(res.data?.incoming_email_logo_url ?? null);
		}).finally(() => {
			setLogoUploading(false);
			if (logoInputRef.current) {
				logoInputRef.current.value = '';
			}
		});
	};

	const handleLogoRemove = () => {
		if (logoUploading) return;

		setLogoUploading(true);
		axios.delete(`${baseURL}emails/incoming-logo`, {
			headers: { Authorization: `Bearer ${Cookies.get('access_token')}` },
		}).then(() => {
			setCompanyLogoUrl(null);
		}).finally(() => {
			setLogoUploading(false);
		});
	};

	const isLoading = useSelector(state => state.emailsReducer.loading);
	const rows = useSelector(state => {
		let tempEmails = state?.emailsReducer?.email;
		return processRows(tempEmails);
	});

	const showFormModal = (title, item = defaultFormInputs) => {
		setFormData({ title, item })
		setShowItemModal(true);
	};

	return (
		<MainContainer>
			<Container fluid="sm">
				<Row className='pt-4 pb-3'>
					<Col sm={6} className='text-align-left'>
						<h5>Incoming Emails</h5>
					</Col>
					<Col sm={6} className="d-flex justify-content-end align-items-center gap-2">
						<input
							ref={logoInputRef}
							type="file"
							accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
							className="d-none"
							onChange={handleLogoSelect}
						/>
						{logoSettingsLoaded && (companyLogoUrl ? (
							<div className="position-relative d-inline-block" title="Company logo">
								<div
									className="border rounded bg-light d-flex align-items-center justify-content-center"
									style={{ padding: '8px 14px', minHeight: 48, minWidth: 72 }}
								>
									<img
										src={companyLogoUrl}
										alt="Company logo"
										style={{ height: 36, maxWidth: 120, objectFit: 'contain', display: 'block', opacity: logoUploading ? 0.5 : 1 }}
									/>
								</div>
								<div
									className="position-absolute d-flex"
									style={{ top: -7, right: -7, gap: 4 }}
								>
									<Button
										variant="light"
										size="sm"
										className="border shadow-sm p-1 lh-1"
										onClick={openLogoPicker}
										disabled={logoUploading}
										title="Replace logo"
									>
										<BsPencil size={13} />
									</Button>
									<Button
										variant="light"
										size="sm"
										className="border shadow-sm p-1 lh-1 text-danger"
										onClick={handleLogoRemove}
										disabled={logoUploading}
										title="Remove logo"
									>
										<BsTrash3 size={13} />
									</Button>
								</div>
							</div>
						) : (
							<Button
								variant="outline-primary"
								onClick={openLogoPicker}
								disabled={logoUploading}
								className="d-inline-flex align-items-center"
							>
								<BsImage className="me-1" />
								{logoUploading ? 'Uploading…' : 'Add Company Logo'}
							</Button>
						))}
						<Button variant='primary' onClick={() => showFormModal('Add')}>
							<img src={plus} />
							<span className='ms-1'>Add Email</span>
						</Button>
					</Col>
				</Row>
				<Row>
					<Col xs="12" lg="4" className="mb-3">
						<IncomingEmailTicketRecipient />
					</Col>
					<Col xs="12" lg="8" className="overflow-x-auto">
						<TableView rows={rows} columns={columns} tableClass={'horizontal-line-table bordered-table'} isLoading={isLoading} />
					</Col>
				</Row>
			</Container>
			{
				showItemModal && <ItemModal currentState={formData} itemType={itemType} single={single} prefixAPI={prefixAPI} reduxRefresh={reduxRefresh} closeModal={() => setShowItemModal(false)} />
			}
		</MainContainer>
	);
}
export default Emails
